import { PureComponent } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  Heading,
  Modal,
  ModalOverlay,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';

import { type Account } from '../model';

import './CancelPlanButton.scss';

type CancelPlanButtonProps = Readonly<{
  onCancelSubscription: (account: Account, endImmediately: boolean) => void;
  account: Account;
  cancelImmediatelyIsEnabled: boolean;
}>;

type CancelPlanButtonState = Readonly<{
  isOpen: boolean;
  isCheckboxChecked: boolean;
}>;

export class CancelPlanButton extends PureComponent<CancelPlanButtonProps, CancelPlanButtonState> {
  public state: CancelPlanButtonState = {
    isOpen: false,
    isCheckboxChecked: false,
  };

  public render() {
    const { onCancelSubscription, account, cancelImmediatelyIsEnabled } = this.props;
    const { isOpen, isCheckboxChecked } = this.state;

    return (
      <>
        <Button
          variant="destructive"
          onPress={() => this.setState({ isOpen: true })}
          className="cancel-plan-button__trigger"
        >
          Cancel Plan
        </Button>
        <ModalOverlay isOpen={isOpen} onOpenChange={(open) => !open && this.setState({ isOpen: false })}>
          <Modal>
            <Dialog>
              <Heading slot="title">Cancel Immediately or At End of Subscription?</Heading>
              <div className="cancel-plan-button__content">
                <p className="cancel-plan-button__title">Cancel Immediately or At End of Subscription?</p>
                <p>Cancel at end of subscription: Standard option appropriate for most use situations</p>
                <p>
                  Cancel immediately: Only for use in specific situations where the customer is past due.
                  <br />
                  <span className="cancel-plan-button__warning">For Finance and Internal Systems use only</span>
                </p>
                <Checkbox
                  isSelected={isCheckboxChecked}
                  onChange={(checked) => this.setState({ isCheckboxChecked: checked })}
                >
                  Confirm you want to cancel this account
                </Checkbox>
              </div>
              <div className="cancel-plan-button__actions">
                {!isCheckboxChecked ? (
                  <TooltipTrigger>
                    <Button
                      variant="destructive"
                      onPress={() => this.handleConfirm(onCancelSubscription, account, false)}
                      className={`cancel-plan-button__end-subscription ${isCheckboxChecked ? 'cancel-plan-button__button--enabled' : 'cancel-plan-button__button--disabled'}`}
                      isDisabled={!isCheckboxChecked}
                    >
                      Cancel At End of Subscription
                    </Button>
                    <Tooltip placement="top" variant="popover">
                      Confirm you really want to cancel this plan
                    </Tooltip>
                  </TooltipTrigger>
                ) : (
                  <Button
                    variant="destructive"
                    onPress={() => this.handleConfirm(onCancelSubscription, account, false)}
                    className={`cancel-plan-button__end-subscription ${isCheckboxChecked ? 'cancel-plan-button__button--enabled' : 'cancel-plan-button__button--disabled'}`}
                    isDisabled={!isCheckboxChecked}
                  >
                    Cancel At End of Subscription
                  </Button>
                )}

                {cancelImmediatelyIsEnabled &&
                  (!isCheckboxChecked ? (
                    <TooltipTrigger>
                      <Button
                        variant="destructive"
                        onPress={() => this.handleConfirm(onCancelSubscription, account, true)}
                        isDisabled={!isCheckboxChecked}
                        className="cancel-plan-button__immediately cancel-plan-button__button--auto-margin"
                      >
                        Cancel Immediately
                      </Button>
                      <Tooltip placement="top" variant="popover">
                        Confirm you really want to cancel this plan
                      </Tooltip>
                    </TooltipTrigger>
                  ) : (
                    <Button
                      variant="destructive"
                      onPress={() => this.handleConfirm(onCancelSubscription, account, true)}
                      isDisabled={!isCheckboxChecked}
                      className="cancel-plan-button__immediately cancel-plan-button__button--auto-margin"
                    >
                      Cancel Immediately
                    </Button>
                  ))}
              </div>
            </Dialog>
          </Modal>
        </ModalOverlay>
      </>
    );
  }

  private handleConfirm(
    onCancelSubscription: (account: Account, endImmediately: boolean) => void,
    account: Account,
    endImmediately: boolean,
  ) {
    onCancelSubscription(account, endImmediately);
    this.setState({ isOpen: false, isCheckboxChecked: false });
  }
}
